import React from 'react';
import {useQuery} from '@tanstack/react-query';
import {useParams, useNavigate} from 'react-router-dom';
import {comunicacionesPublicasApi, contratosPublicosApi, proyectosPublicosApi, reunionesPublicasApi} from '../../../api/api';
import type {
    ProyectoContratoMayorDTO,
    ProyectoDTO,
    PublicoContratoMayorDTO
} from '../../../api/Objetos';
import {ApiError} from '../../../api/http';
import {normalizeProyectoForm, toFormDTO} from '../../admin/projects/projectFormUtils';
import {ProjectForm} from '../../admin/projects/ProjectForm';
import { API_BASE_URL } from '../../../api/config';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { slugify } from '../../shared/utils/slugify';
import { ensureArray } from '../../shared/utils/api';
import {PublicProjectMedia} from '../components/PublicProjectMedia';

export function PublicProjectDetailPage() {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { data: projects, isLoading, isFetching } = useQuery<ProyectoDTO[]>({
        queryKey: ['public', 'proyectos'],
        queryFn: () => proyectosPublicosApi.list(),
        select: (items: unknown) => ensureArray<ProyectoDTO>(items).map(p => ({...p, slug: slugify(p.informacionGeneral?.denominacion ?? '')})),
    });

    const isResolvingId = isLoading || isFetching;

    if (isResolvingId) {
        return (
            <div className="container">
                <div className="projectLoading">
                    <p>{t('publicProjectDetail.loading')}</p>
                </div>
            </div>
        );
    }

    const proyecto = projects?.find((p: ProyectoDTO) => p.slug === slug);

    if (!proyecto || !proyecto.id) {
        window.alert(t('publicProjectDetail.projectNotFound'));
        navigate('/');
        return null;
    }

    return <ProjectDetails id={proyecto.id} />;
}

function ProjectDetails(props: { id: number }) {
    const { t } = useTranslation();
    const { id } = props;

    const proyectoQ = useQuery({
        queryKey: ['public', 'proyectos', 'detail', id],
        queryFn: async () => {
            const [proyectoBase, datosContratosPublicos, comunicacionesPublicas, reunionesPublicas] = await Promise.all([
                proyectosPublicosApi.get(id!),
                contratosPublicosApi.listar(id!).catch(() => null),
                comunicacionesPublicasApi.listar(id!),
                reunionesPublicasApi.listar(id!).catch(() => []),
            ]);

            const contratosMayores = (datosContratosPublicos?.contratosMayores ?? []).map(mapPublicContratoMayor);

            return {
                ...proyectoBase,
                informacionGeneral: proyectoBase?.informacionGeneral,
                comunicacionProyecto: comunicacionesPublicas.map(item => ({...item, publico: true})),
                reuniones: reunionesPublicas.map(item => ({...item, publico: true})),
                datosContratos: datosContratosPublicos ? {
                    ...(proyectoBase?.datosContratos ?? {}),
                    publico: true,
                    contratoMayores: contratosMayores,
                    contratoMenores: [],
                    gastosImputadosFueraDeLaSubvencion: [],
                } : {publico: false, contratoMayores: [], contratoMenores: [], gastosImputadosFueraDeLaSubvencion: []},
            };
        },
        enabled: !!id,
        select: (value) => normalizeProyectoForm(filterPublicProjectData(toFormDTO(value))),
    });

    if (proyectoQ.isLoading) {
        return (
            <div className="container">
                <div className="projectLoading">
                    <p>{t('publicProjectDetail.loading')}</p>
                </div>
            </div>
        );
    }

    if (proyectoQ.isError) {
        return (
            <div className="container">
                <div className="alert alertError">
                    <strong>{t('publicProjectDetail.errorTitle')}</strong>
                    <div className="muted">{renderProjectError(proyectoQ.error, t)}</div>
                </div>
            </div>
        );
    }

    if (!proyectoQ.data) {
        return (
            <div className="container">
                <div className="alert alertError">
                    <strong>{t('publicProjectDetail.notAvailable')}</strong>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="stackLg">
                <PublicProjectMedia proyectoId={id}/>
                <ProjectForm
                    projectId={id!}
                    initialValue={proyectoQ.data}
                    onSubmit={() => {}}
                    submitLabel=""
                    draftKey={`public-project-${id}`}
                    readOnly={true}
                    showIds={false}
                />
            </div>
        </div>
    );
}

function isPublicVisible(value?: unknown) {
    return value === true || value === 'true' || value === 1 || value === '1';
}

function filterPublicProjectData(value: ReturnType<typeof toFormDTO>) {
    return {
        ...value,
        informacionGeneral: isPublicVisible(value.informacionGeneral?.publico) ? value.informacionGeneral : {},
        requerimientos: (value.requerimientos ?? []).filter((item) => isPublicVisible(item.publico)),
        datosContratos: isPublicVisible(value.datosContratos?.publico)
            ? {
                ...value.datosContratos,
                contratoMayores: (value.datosContratos?.contratoMayores ?? []).filter((item) => isPublicVisible(item.publico)),
                contratoMenores: (value.datosContratos?.contratoMenores ?? []).filter((item) => isPublicVisible(item.publico)),
                gastosImputadosFueraDeLaSubvencion: (value.datosContratos?.gastosImputadosFueraDeLaSubvencion ?? [])
                    .filter((item) => isPublicVisible(item.publico)),
            }
            : {
                ...value.datosContratos,
                contratoMayores: [],
                contratoMenores: [],
                gastosImputadosFueraDeLaSubvencion: [],
            },
        datosContables: isPublicVisible(value.datosContables?.publico) ? value.datosContables : {},
        seguimientoPlataforma: isPublicVisible(value.seguimientoPlataforma?.publico) ? value.seguimientoPlataforma : {},
        seguimientoCoFFE: isPublicVisible(value.seguimientoCoFFE?.publico) ? value.seguimientoCoFFE : {},
        certificacionesObra: isPublicVisible(value.certificacionesObra?.publico) ? value.certificacionesObra : {},
        autorizacionesSolicitadas: (value.autorizacionesSolicitadas ?? []).filter((item) => isPublicVisible(item.publico)),
        hitosYObjetivos: (value.hitosYObjetivos ?? []).filter((item) => isPublicVisible(item.publico)),
        participacionCiudadana: (value.participacionCiudadana ?? []).filter((item) => isPublicVisible(item.publico)),
        comunicacionProyecto: (value.comunicacionProyecto ?? []).filter((item) => isPublicVisible(item.publico)),
        plazosEjecucionJustificacion: (value.plazosEjecucionJustificacion ?? []).filter((item) => isPublicVisible(item.publico)),
        reuniones: (value.reuniones ?? []).filter((item) => isPublicVisible(item.publico)),
        informesGestion: (value.informesGestion ?? []).filter((item) => isPublicVisible(item.publico)),
    };
}

function renderProjectError(error: unknown, t: TFunction<"translation", undefined>) {
    if (error instanceof ApiError) {
        if (error.status === 0) {
            return t('publicProjectDetail.apiErrorConnect', { url: API_BASE_URL });
        }
        return `${error.status}: ${error.message}`;
    }

    if (error instanceof Error) return error.message;
    return t('publicProjectDetail.apiErrorUnknown');
}

function mapPublicContratoMayor(input: PublicoContratoMayorDTO): ProyectoContratoMayorDTO {
    return {
        descripcion: input.descripcion,
        urlPliego: input.urlPliego,
        proyectoSubcontratistas: [],
        publico: true,
    };
}
